import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../../hooks/useApi";
import { FaArrowDown, FaArrowUp, FaBan, FaCheck, FaChevronLeft, FaChevronRight, FaFileCsv, FaFileExcel, FaFileImport, FaPlus, FaSave, FaSearch, FaTimes, FaTrash, FaUnlink } from "react-icons/fa";
import { IoEllipsisVerticalSharp } from "react-icons/io5";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AsyncSelect, TextInput } from "../forms/Inputs";
import { Form } from "../forms/Form";
import { Popup } from "../layout/Popup";
import { useOptions } from "../../hooks/useOptions";
import { Button } from "../layout/Button";
import { Menu, MenuLink } from "../layout/Menu";
import { useTranslations } from "../../layers/Localization";
import { TabbedCard } from "./Tabs";
import { useObjectState } from "../../hooks/useObjectState";
import { debounce } from "lodash";

export function ListPage({ title, model, columns, sort, join, actions, mainActions, filter, relation, m2m, root, disabled }) {
    const fullColumns = Object.fromEntries(Object.entries(columns).map(([key, column]) =>
        [key, typeof column === 'string' ? { sort: column, value: item => item[column] } : column]
    ))
    const fullKeys = [...new Set(Object.values(fullColumns).map(x => x.sort).filter(x => x))]

    const [items, setItems] = useState([])
    const [count, setCount] = useState(null)
    const [searchString, setSearchString] = useState("")
    const [page, setPage] = useState(1)
    const [limit] = useState(15)

    const [linkPopup, setLinkPopup] = useState(false)
    const [unlinkPopup, setUnlinkPopup] = useState(false)
    const [deletePopup, setDeletePopup] = useState(false)
    const [relatedItemId, setRelatedItemId] = useState(null)
    const relatedOptions = useOptions(model, fullKeys)

    const [tab, setTab] = useState(filter?.constructor === Object ? Object.keys(filter)[0] : null)
    const getDefaultSort = () =>
        sort && typeof sort === 'function' ? sort(tab)
            : sort ? { ...sort }
                : Object.values(fullColumns)[0]?.sort ? { [Object.keys(fullColumns)[0]]: 'asc' } : {}
    const [currentSort, setCurrentSort] = useObjectState(getDefaultSort())
    const [isDefaultSort, setIsDefaultSort] = useState(true)

    const api = useApi()
    const navigate = useNavigate()
    const { id } = useParams()
    const { pathname, search } = useLocation()
    const importInput = useRef()
    const t = useTranslations()

    const filterQuery = [
        Object.keys(currentSort).length && Object.entries(currentSort).map(([key, order]) => `orderBy:${order === 'asc' ? 'asc' : 'desc'}=${fullColumns[key]?.sort}`).join('&'),
        tab ? filter[tab] : filter ? filter : null,
        relation ? `${relation}=${encodeURIComponent(id)}` : null,
        join ? Object.entries(join).map(([key, value]) => (value instanceof Array ? value : [value]).map(value => `${encodeURIComponent(key)}:join=${encodeURIComponent(value)}`).join('&')).join('&') : null,
    ].filter(x => x).join('&')
    const searchQuery = [
        searchString?.length >= 10 ? `or:id:contains=${encodeURIComponent(searchString)}` : null,
        searchString ? fullKeys.map(x => `or:${encodeURIComponent(x)}:contains=${encodeURIComponent(searchString)}`).join('&') : null,
    ].filter(x => x).join('&')
    const fullQuery = [filterQuery, searchQuery].filter(x => x).join('&')

    const prependLink = typeof root === 'function' ? root(tab) : root || ''
    const appendQuery = [
        root ? `?next=${encodeURIComponent(pathname + search)}` : '',
        relation && !m2m ? `?default=${encodeURIComponent(JSON.stringify({ [relation]: id }))}` : ''
    ].filter(x => x).join('&')

    const loadItemsDirectly = useCallback(async fullQuery => {
        if (id === 'new') return
        await Promise.all([
            api.get(`/${model}?${fullQuery}&page=${page}&limit=${limit}`).then(setItems),
            api.get(`/${model}/count?${fullQuery}`).then(x => setCount(Number(x)))
        ])
    }, [page, limit, model])
    const loadItems = () => loadItemsDirectly(fullQuery)
    const debouncedLoadItems = useCallback(debounce(loadItemsDirectly, 250, { leading: false, trailing: true }), [loadItemsDirectly])
    const changeSearch = async search => {
        setPage(1)
        setSearchString(search)
    }
    const changeSort = async key => {
        setIsDefaultSort(false)
        setCurrentSort(currentSort[key] ? { ...currentSort, [key]: currentSort[key] === 'asc' ? 'desc' : 'asc' } : { [key]: 'asc' })
    }
    const add = () => {
        if (relation && m2m) {
            setLinkPopup(true)
            setRelatedItemId(null)
        } else navigate(`${prependLink}/${model}/new${appendQuery}`)
    }
    const link = async () => {
        await api.post(`/${model}/${relatedItemId}/${relation}/${id}`)
        loadItems()
        setLinkPopup(false)
        setRelatedItemId(null)
    }
    const openUnlink = item => {
        setRelatedItemId(item.id)
        setUnlinkPopup(true)
    }
    const unlink = async () => {
        await api.delete(`/${model}/${relatedItemId}/${relation}/${id}`)
        loadItems()
        setUnlinkPopup()
        setRelatedItemId(null)
    }
    const openDelete = item => {
        setRelatedItemId(item.id)
        setDeletePopup(true)
    }
    const remove = async () => {
        await api.delete(`/${model}/${relatedItemId}`)
        loadItems()
        setDeletePopup()
        setRelatedItemId(null)
    }
    const importFile = async () => {
        await api.post(`/${model}/import/${importInput.current.files[0].name.split('.').slice(-1)[0]}`, { file: importInput.current.files[0] })
        loadItems()
    }
    const formatValue = value => {
        if (value instanceof Date) {
            if (value.getUTCHours() + value.getUTCMinutes() + value.getUTCSeconds() + value.getUTCMilliseconds() === 0) return new Date(value.getTime() + value.getTimezoneOffset() * 60 * 1000).toLocaleDateString()
            return value.toLocaleString()
        }
        if (typeof value === 'boolean') return value === true ? <FaCheck className="mt-1 text-green-600" /> : <FaTimes className="mt-1 text-red-600 text-[18px]" />
        return value
    }

    const fullActions = {
        ...(disabled ? {} : relation && m2m
            ? { [t('Unlink')]: openUnlink }
            : { [t('Delete')]: openDelete }
        ),
        ...actions
    }

    useEffect(() => {
        if (filter?.constructor === Object && !Object.keys(filter).includes(tab)) setTab(Object.keys(filter)[0])
    }, [filter])

    useEffect(() => {
        setTimeout(() => {
            if (isDefaultSort) setCurrentSort(getDefaultSort(tab))
            if (page !== 1) setPage(1)
        })
    }, [tab, relation, id])

    useEffect(() => {
        loadItems()
    }, [page, limit, filterQuery])
    useEffect(() => {
        debouncedLoadItems(fullQuery)
    }, [searchQuery])

    if (relation && id === 'new') return

    return <div className="w-full">
        {title && !relation && <h1 className={`font-bold text-2xl md:w-full mb-3`}>{title}</h1>}
        <TabbedCard tabs={Object.keys(filter?.constructor === Object ? filter : {})} tab={tab} setTab={setTab}>
            <div className="overflow-auto rounded-t-xl flex gap-3 px-5 py-[.44rem] border-b border-gray-300">
                <Form onSubmit={loadItems} className="grow">
                    <TextInput value={searchString} onChange={changeSearch} placeholder={t('Search')} icon={<FaSearch />} onIconClick={loadItems} />
                </Form>
                <Button icon={<FaPlus />} onClick={disabled ? undefined : add} disabled={disabled}>{t('Add')}</Button>
                <div className="flex items-center">
                    <input ref={importInput} type="file" className="hidden" onChange={importFile} accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" />
                    <Menu activator={<button className="rounded-full p-2 flex items-center hover:bg-gray-300"><IoEllipsisVerticalSharp className="text-lg" /></button>}>
                        {(!relation || !m2m) && <>
                            <MenuLink onClick={() => importInput.current.click()} icon={<FaFileImport className="text-green-600" />}>{t('Import')}</MenuLink>
                            <MenuLink href={`${process.env.REACT_APP_API_URL}/${model}/export/csv`} icon={<FaFileCsv className="text-green-600" />}>{t('Export CSV')}</MenuLink>
                            <MenuLink href={`${process.env.REACT_APP_API_URL}/${model}/export/xlsx`} icon={<FaFileExcel className="text-green-600" />}>{t('Export XLSX')}</MenuLink>
                        </>}

                        {Object.entries(mainActions || {}).map(([label, action]) =>
                            <MenuLink key={label} href={action?.href ? action?.href : undefined} onClick={action?.href ? undefined : () => action(loadItems)}>{label}</MenuLink>
                        )}
                    </Menu>
                </div>
            </div>
            <div className="w-full overflow-auto">
                <div className="w-full [&_*]:align-top grid" style={{ gridTemplateColumns: `repeat(${Object.entries(fullColumns).length + 1}, auto)` }}>
                    {Object.entries(fullColumns).map(([label, column]) =>
                        <div key={label} className={`px-3 py-[0.4rem] first:pl-5 last:pr-5 border-grey-300 font-bold ${count > 0 ? 'border-b' : ''}`}>
                            <div className={`group flex justify-left items-center gap-2 ${column?.sort ? 'cursor-pointer' : ''}`} onClick={column?.sort ? () => changeSort(label) : undefined}>
                                <div>{label}</div>
                                {!column?.sort ? null : !currentSort[label] ? <FaArrowDown className="invisible group-hover:visible opacity-50" /> : currentSort[label] === 'asc' ? <FaArrowDown /> : <FaArrowUp />}
                            </div>
                        </div>
                    )}
                    <div className={`px-3 py-[0.4rem] border-grey-300 font-bold ${count > 0 ? 'border-b' : ''}`}>{t('Actions')}</div>
                    {items.map(item =>
                        <div key={item.id} className="group contents" tabIndex={0}>
                            {Object.entries(fullColumns).map(([label, column]) =>
                                <a key={label} href={`${prependLink}/${model}/${item.id}${appendQuery}`} className="px-3 py-[0.4rem] first:pl-5 last:pr-5 border-b border-grey-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">{formatValue(column.value(item))}</a>
                            )}
                            <div className="px-3 py-[0.4rem] border-b border-grey-300 cursor-pointer group-hover:bg-gray-200 whitespace-nowrap md:whitespace-normal">
                                <div className="flex w-full justify-center items-center">
                                    <Menu activator={<button className="rounded-full p-1 flex items-center hover:bg-gray-300"><IoEllipsisVerticalSharp /></button>}>
                                        {Object.entries(fullActions).map(([label, action]) =>
                                            <MenuLink key={label} href={action?.href ? action?.href(item) : undefined} onClick={action?.href ? undefined : () => action(item, loadItems)}>{label}</MenuLink>
                                        )}
                                    </Menu>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {count > 0 && <div className="mx-5 my-2 flex justify-end items-center gap-3">
                <div>{t('{start} - {end} of {count}', { start: (page - 1) * limit + (items.length ? 1 : 0), end: (page - 1) * limit + items.length, count })}</div>
                {page > 1 && <button onClick={() => setPage(page - 1)}><FaChevronLeft /></button>}
                {page < Math.ceil(count / limit) && <button onClick={() => setPage(page + 1)}><FaChevronRight /></button>}
            </div>}
        </TabbedCard>
        <Popup value={linkPopup} onChange={setLinkPopup}>
            <Form onSubmit={link}>
                <h2 className="text-xl mb-5">{t('Select an item to link')}</h2>
                <AsyncSelect value={relatedItemId} onChange={setRelatedItemId} options={relatedOptions} required />
                <div className="mt-5 flex gap-3">
                    <Button gray icon={<FaBan />} onClick={() => setLinkPopup(false)}>{t('Cancel')}</Button>
                    <Button icon={<FaSave />} submit>{t('Save')}</Button>
                </div>
            </Form>
        </Popup>
        <Popup value={unlinkPopup} onChange={setUnlinkPopup}>
            <h2 className="text-xl mb-5">{t('Are you sure you want to unlink this item ?')}</h2>
            <div className="flex gap-3">
                <Button gray icon={<FaBan />} onClick={() => setUnlinkPopup(false)}>{t('Cancel')}</Button>
                <Button red icon={<FaUnlink />} onClick={unlink}>{t('Unlink')}</Button>
            </div>
        </Popup>
        <Popup value={deletePopup} onChange={setDeletePopup}>
            <h2 className="text-xl mb-5">{t('Are you sure you want to delete this item ?')}</h2>
            <div className="flex gap-3">
                <Button gray icon={<FaBan />} onClick={() => setDeletePopup(false)}>{t('Cancel')}</Button>
                <Button red icon={<FaTrash />} onClick={remove}>{t('Delete')}</Button>
            </div>
        </Popup>
    </div>
}

export function Badge({ value, options }) {
    const option = options.find(x => x.value === value)
    if (!option) return value
    return <div className="flex">
        <div style={{ backgroundColor: option.color }} className="text-center text-white rounded-full text-sm px-2 py-[2px]">{option.label}</div>
    </div>
}

export function FkLink({ children, to }) {
    return <div className="flex">
        <div className={`underline ${to ? 'text-blue-500 hover:text-blue-600' : ''}`}>
            {to ? <Link to={to}>{children}</Link> : children}
        </div>
    </div>
}